import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * OnboardingNudge — tiny SVG progress ring for the topbar.
 * • Hidden when everything is resolved.
 * • Fetches /api/onboarding/state at mount; polls quietly every 2 min
 *   so it self-heals when the user completes a step in another tab.
 * • Zero business logic, purely visual — links to /onboarding.
 */
export default function OnboardingNudge() {
    const [pct, setPct] = useState(null);
    const [done, setDone] = useState(0);
    const [total, setTotal] = useState(0);

    const load = async () => {
        try {
            const r = await api.get("/api/onboarding/state");
            setPct(r.data.percent_resolved);
            setDone(r.data.done);
            setTotal(r.data.total);
        } catch {
            setPct(null);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 120_000);
        return () => clearInterval(t);
    }, []);

    if (pct == null || pct >= 100) return null;

    const R = 10;
    const C = 2 * Math.PI * R;
    const off = C - (Math.max(0, Math.min(100, pct)) / 100) * C;

    return (
        <Link to="/onboarding" data-testid="onboarding-nudge"
            title={`Build your VAULT — ${done}/${total} complete`}
            className="hidden md:inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-stone-200 hover:border-stone-300 hover:bg-stone-50 transition-colors">
            <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden>
                <circle cx="13" cy="13" r={R} fill="none"
                    stroke="rgba(11,30,63,0.10)" strokeWidth="3" />
                <circle cx="13" cy="13" r={R} fill="none"
                    stroke="#c9a253" strokeWidth="3" strokeLinecap="round"
                    strokeDasharray={C} strokeDashoffset={off}
                    transform="rotate(-90 13 13)"
                    style={{ transition: "stroke-dashoffset 500ms ease" }} />
            </svg>
            <span className="text-xs text-stone-700 leading-none">
                <span className="font-semibold">{done}</span>
                <span className="text-stone-500">/{total}</span>
                <span className="hidden xl:inline text-stone-500 ml-1">setup</span>
            </span>
        </Link>
    );
}
